// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Badge, Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import type { Appointment, Encounter } from '@medplum/fhirtypes';
import { IconLink, IconVideo } from '@tabler/icons-react';
import type { JSX } from 'react';
import { Link } from 'react-router';
import { getAppointmentTypeDisplay, getPatientFromAppointment } from '../../utils/appointment-formatting';
import { getVisitOutcomeLabel } from '../../utils/encounter';
import { getTelehealthInfo } from '../../utils/telehealth';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';

const BRAND = '#640040';

interface EncounterHeaderProps {
  encounter: Encounter;
  appointment?: Appointment;
  onConclude?: () => void;
}

export const EncounterHeader = ({ encounter, appointment, onConclude }: EncounterHeaderProps): JSX.Element => {
  const status = encounter.status;
  const telehealthInfo = appointment ? getTelehealthInfo(appointment) : undefined;
  const isActiveStatus = status === 'planned' || status === 'arrived' || status === 'in-progress';

  if (isActiveStatus) {
    const appointmentTitle = appointment
      ? `${getAppointmentTypeDisplay(appointment)} with ${getPatientFromAppointment(appointment).name}`
      : 'Encounter';

    return (
      <Paper withBorder radius={8} p={20} mx="md" mt="md">
        <Stack gap={8}>
          <Anchor component={Link} to="/schedule" fw={700} fz={12} c={BRAND} td="none">
            Appointments &gt;
          </Anchor>
          <Text fw={700} style={{ fontSize: 40, lineHeight: '48px' }}>
            {appointmentTitle}
          </Text>
          {appointment?.start && appointment?.end && (
            <AppointmentTimeWithTimezone
              start={appointment.start}
              end={appointment.end}
              textProps={{ fw: 700, fz: 20, lh: '24px' }}
            />
          )}
          <Box h={8} />
          <Group gap={8}>
            {telehealthInfo ? (
              <Button
                component="a"
                variant="outline"
                href={telehealthInfo.joinUrl ?? telehealthInfo.startUrl}
                target="_blank"
                rel="noopener noreferrer"
                radius={4}
                h={42}
                style={{ borderColor: BRAND, color: BRAND }}
              >
                <IconLink size={16} />
              </Button>
            ) : (
              <Button color="blue" radius={4} h={42} leftSection={<IconVideo size={16} />} disabled>
                Zoom link pending...
              </Button>
            )}
            <Button radius={4} h={42} style={{ backgroundColor: BRAND }} onClick={onConclude}>
              Conclude Session
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  // The visit is over. Say so explicitly — this header previously rendered a bare
  // "Encounter Note" with no indication of whether the session had been concluded.
  const outcomeLabel = getVisitOutcomeLabel(encounter, appointment);

  return (
    <Paper withBorder radius={8} p={20} mx="md" mt="md">
      <Stack gap={8}>
        <Anchor component={Link} to="/schedule" fw={700} fz={12} c={BRAND} td="none">
          Appointments &gt;
        </Anchor>
        <Group gap={12} align="center">
          <Text fw={700} style={{ fontSize: 40, lineHeight: '48px' }}>
            Encounter Note
          </Text>
          {outcomeLabel && (
            <Badge size="lg" variant="light" color={BRAND} aria-label={`Visit ${outcomeLabel}`}>
              {outcomeLabel}
            </Badge>
          )}
        </Group>
      </Stack>
    </Paper>
  );
};
